import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtMY, KIND_LABEL } from "@/lib/date";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, Trash, FilePdf, FunnelSimple } from "@phosphor-icons/react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function downloadPdf(id) {
  const token = localStorage.getItem("cosec_token");
  window.open(`${API_BASE}/reminders/${id}/pdf?auth=${token}`, "_blank");
}

const KIND_OPTIONS = [
  { value: "annual_return", label: "Annual Return" },
  { value: "audit_submission", label: "Accounts to Audit" },
  { value: "audited_report", label: "Audited Report Filing" },
];

export default function Reminders() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const [clientFilter, setClientFilter] = useState("__all__");
  const [kindFilter, setKindFilter] = useState("__all__");

  const load = () => api.get("/reminders?limit=500").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this reminder log entry?")) return;
    await api.delete(`/reminders/${id}`);
    toast.success("Deleted");
    load();
  };

  const clientOptions = useMemo(() => {
    const seen = new Map();
    for (const r of items) {
      const key = r.client_id || r.ssm_no || r.client_name || "__unknown__";
      if (!seen.has(key)) {
        seen.set(key, {
          value: key,
          label: r.client_name || r.ssm_no || "Unknown client",
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const filtered = useMemo(() => items.filter(r => {
    if (kindFilter !== "__all__" && r.kind !== kindFilter) return false;
    if (clientFilter !== "__all__") {
      const key = r.client_id || r.ssm_no || r.client_name || "__unknown__";
      if (key !== clientFilter) return false;
    }
    return true;
  }), [items, clientFilter, kindFilter]);

  const filtersActive = clientFilter !== "__all__" || kindFilter !== "__all__";

  return (
    <div className="p-10 max-w-[1400px]">
      <header className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Activity</div>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight">Reminder Log</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl">
          Every reminder emailed to directors is recorded here — click <b>View</b> to see the exact email that was sent.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3 border border-border p-4">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground pr-2">
          <FunnelSimple size={14} weight="duotone" /> Filter
        </div>
        <div className="flex flex-col">
          <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Client</label>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="reminder-client-filter" className="rounded-sm w-[240px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All clients</SelectItem>
              {clientOptions.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col">
          <label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Deadline type</label>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger data-testid="reminder-kind-filter" className="rounded-sm w-[220px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All deadlines</SelectItem>
              {KIND_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {filtersActive && (
          <Button variant="ghost" size="sm" className="h-9 rounded-sm font-mono text-[11px] uppercase tracking-widest"
                  data-testid="reminder-clear-filters"
                  onClick={() => { setClientFilter("__all__"); setKindFilter("__all__"); }}>
            Clear
          </Button>
        )}
        <div className="ml-auto text-xs text-muted-foreground font-mono" data-testid="reminder-count">
          {filtered.length} of {items.length} shown
        </div>
      </div>

      <div className="border border-border">
        <table className="w-full text-sm" data-testid="reminders-table">
          <thead>
            <tr className="text-left border-b border-border">
              <Th>Sent At</Th><Th>Client</Th><Th>Deadline</Th><Th>Recipients</Th><Th>Sent By</Th><Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-8 text-sm text-muted-foreground">
                {items.length === 0 ? "No reminders sent yet." : "No reminders match the current filters."}
              </td></tr>
            )}
            {filtered.map(r => (
              <tr key={r.id} className="data-row border-b border-border">
                <td className="px-6 py-3 font-mono text-xs">
                  {fmtMY(r.sent_at)} · {String(r.sent_at).slice(11, 16)}
                </td>
                <td className="px-6 py-3">
                  <div>{r.client_name || "—"}</div>
                  {r.ssm_no && <div className="text-[11px] font-mono text-muted-foreground">{r.ssm_no}</div>}
                </td>
                <td className="px-6 py-3">{KIND_LABEL[r.kind] || r.kind}</td>
                <td className="px-6 py-3 font-mono text-xs">
                  {(r.recipients || []).length === 0
                    ? <span className="text-muted-foreground">none</span>
                    : (r.recipients || []).map((e, i) => <div key={i}>{e}</div>)}
                </td>
                <td className="px-6 py-3 text-xs">{r.sent_by}</td>
                <td className="px-6 py-3 text-right space-x-1">
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setView(r)}
                          data-testid={`view-reminder-${r.id}`}>
                    <Eye size={14} weight="bold" className="mr-1.5" /> View
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => downloadPdf(r.id)}
                          data-testid={`pdf-reminder-${r.id}`}>
                    <FilePdf size={14} weight="bold" className="mr-1.5" /> PDF
                  </Button>
                  <Button size="sm" variant="ghost" className="text-[#DA1E28] h-8"
                          onClick={() => remove(r.id)} data-testid={`delete-reminder-${r.id}`}>
                    <Trash size={14} weight="bold" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!view} onOpenChange={o => !o && setView(null)}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{view?.subject || "Reminder email"}</DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-4">
              <div className="border border-border p-4 font-mono text-xs space-y-1 bg-[#F9F9F9]">
                <div><span className="text-muted-foreground uppercase tracking-widest text-[10px] mr-2">From</span> Specfield Management Services Sdn. Bhd.</div>
                <div><span className="text-muted-foreground uppercase tracking-widest text-[10px] mr-2">To</span> {(view.recipients || []).join(", ") || "—"}</div>
                <div><span className="text-muted-foreground uppercase tracking-widest text-[10px] mr-2">Subject</span> {view.subject || "—"}</div>
                <div><span className="text-muted-foreground uppercase tracking-widest text-[10px] mr-2">Sent</span> {fmtMY(view.sent_at)} · {String(view.sent_at).slice(11, 16)} UTC · by {view.sent_by}</div>
                {view.errors && view.errors.length > 0 && (
                  <div><span className="text-[#DA1E28] uppercase tracking-widest text-[10px] mr-2">Failed</span> {view.errors.join(", ")}</div>
                )}
              </div>
              <div className="border border-border">
                <div className="px-4 py-2 border-b border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Email preview
                </div>
                <div className="p-4 bg-white" data-testid="reminder-email-preview"
                     dangerouslySetInnerHTML={{ __html: view.html_body || "<em class='text-muted-foreground'>No body captured</em>" }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children, className = "" }) =>
  <th className={`px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal ${className}`}>{children}</th>;
